import { useState } from 'react';
import PropTypes from 'prop-types';
import { InputControlled } from 'components/UI/InputControlled/InputControlled';
import { DropdownWithMenu } from 'components/UI/DropdownWithMenu/DropdownWithMenu';
import { ReactComponent as HelpIcon } from 'assets/icons/svgs/icon-help.svg';
import useWindowDimensions from 'hooks/use-window-dimensions';
import { useFormContext } from 'react-hook-form';

export const ContactFormSection = (props) => {
  const {
    Account: { Title, DOBDay, DOBMonth, DOBYear },
    Prefixes,
    DateOfBirth,
    setSelectedPrefix,
    setSelectedDobDay,
    setSelectedDobMonth,
    setSelectedDobYear,
    setUpdateEmail,
    requestChange,
    setRequestChange,
    retainPasswordFocus,
    setRetainPasswordFocus,
  } = props;
  const [openPopup, setOpenPopup] = useState(false);
  const { isMobile } = useWindowDimensions();
  const popupHintClass = isMobile ? 'hint-visible-m' : 'hint-visible-d';

  const { resetField } = useFormContext();

  const showPopupHandler = (inputId) => {
    if (!isMobile) {
      setOpenPopup(inputId);
    }
  };

  const showPopupMobileHandler = (inputId) => {
    setOpenPopup((prevState) => (prevState ? false : inputId));
  };

  const renderInputHintButton = (inputId) => (
    <button
      type="button"
      className="hint-btn visible-xs"
      onClick={() => showPopupMobileHandler(inputId)}
    >
      <HelpIcon className="registration-form__help-icon" />
    </button>
  );

  const prefixDropDownOptions = Prefixes.map((prefix) => ({
    key: prefix.Description,
    name: prefix.Description,
  }));

  const prefixDropdownUpdateHandler = (prefix) => {
    setSelectedPrefix(prefix);
  };

  const getDobDropDownOptions = (DobOptions) =>
    DobOptions.map((item) => ({
      key: item,
      name: item.toString(),
    }));

  const dobDayDropdownUpdateHandler = (day) => {
    setSelectedDobDay(day);
  };

  const dobMonthDropdownUpdateHandler = (month) => {
    setSelectedDobMonth(month);
  };

  const dobYearDropdownUpdateHandler = (year) => {
    setSelectedDobYear(year);
  };

  const updateEmailClickHandler = (event) => {
    setRequestChange(true);
    setUpdateEmail(true);

    event.preventDefault();
  };

  const cancelUpdateEmailClickHandler = (event) => {
    setRequestChange(false);
    setUpdateEmail(false);
    resetField('updateEmailInputPassword');

    event.preventDefault();
  };

  return (
    <div className="form-section">
      {requestChange ? (
        <>
          <div className="form-subsection">
            <div className="control-label static-label">
              To update your account email address, please enter your password, add your new email
              address into the "Email address" field in the form below, and click "Save". Once your
              account email address has been updated, your new email address will be used to login
              to Arts Centre Melbourne.
            </div>

            <div className="form-group">
              <InputControlled
                type="password"
                id="updateEmailInputPassword"
                labelText="Password"
                hideErrors
                retainFocus={retainPasswordFocus}
                onFocusInputHandler={() => setRetainPasswordFocus(false)}
              />
            </div>
          </div>

          <div className="form-subsection">
            <p className="large">
              <a href="#" onClick={cancelUpdateEmailClickHandler}>
                Cancel email address change
              </a>
            </p>
          </div>
        </>
      ) : (
        <div className="form-subsection">
          <p className="large">
            Do you want to change the email address associated with your account?&nbsp;
            <a href="#" onClick={updateEmailClickHandler}>
              Click here
            </a>
            &nbsp;to update your email address.
          </p>
        </div>
      )}

      <h3 className="h4">Contact information</h3>

      <div className="form-group">
        <DropdownWithMenu
          singleColumn
          hideDefaultItem
          dropdownChangeHandler={prefixDropdownUpdateHandler}
          defaultChosenValue={`${Title || 'Title (optional)'}`}
          defaultValue={`${Title || 'Title (optional)'} `}
          menuData={prefixDropDownOptions}
        />
      </div>

      <div className="form-group">
        <InputControlled type="text" id="regFirstName" labelText="First name" />
      </div>

      <div className="form-group">
        <InputControlled type="text" id="regLastName" labelText="Last name" />
      </div>

      <div className="form-group has-hint">
        <InputControlled
          type="text"
          id="regPhoneNumber"
          labelText="Phone Number"
          showPopupHandler={showPopupHandler}
        />
        {renderInputHintButton('regPhoneNumber')}

        <div className={`form-hint ${openPopup === 'regPhoneNumber' ? popupHintClass : ''}`}>
          <p>
            Please enter at least either your phone number <strong>or</strong> mobile number so we
            can contact you in the event of a change in performance.
          </p>
          <p>
            - Phone Number: <strong>9xxxxxxx</strong>
          </p>
        </div>
      </div>

      <div className="form-group has-hint">
        <InputControlled
          type="text"
          id="regMobileNumber"
          labelText="Mobile Number"
          showPopupHandler={showPopupHandler}
        />

        {renderInputHintButton('regMobileNumber')}

        <div className={`form-hint ${openPopup === 'regMobileNumber' ? popupHintClass : ''}`}>
          <p>
            Please enter at least either your phone number <strong>or</strong> mobile number so we
            can contact you in the event of a change in performance.
          </p>
          <p>
            - Mobile Number: <strong>04xxxxxxxx</strong>
          </p>
        </div>
      </div>

      <div className="form-group">
        <InputControlled
          type="text"
          id="regInputEmail"
          labelText="Email address"
          disabled={!requestChange}
        />
      </div>

      <div className="form-subsection">
        <label className="control-label static-label">Date of birth (optional)</label>
        <p>Let us know your birthday to receive a surprise!</p>
        <div className="row">
          <div className="col-xs-4">
            <DropdownWithMenu
              singleColumn
              hideDefaultItem
              dropdownChangeHandler={dobDayDropdownUpdateHandler}
              defaultChosenValue={`${DOBDay || 'DD'}`}
              menuData={getDobDropDownOptions(DateOfBirth.Days)}
            />
          </div>
          <div className="col-xs-4">
            <DropdownWithMenu
              singleColumn
              hideDefaultItem
              dropdownChangeHandler={dobMonthDropdownUpdateHandler}
              defaultChosenValue={`${DOBMonth || 'MM'}`}
              menuData={getDobDropDownOptions(DateOfBirth.Months)}
            />
          </div>

          <div className="col-xs-4">
            <DropdownWithMenu
              singleColumn
              hideDefaultItem
              dropdownChangeHandler={dobYearDropdownUpdateHandler}
              defaultChosenValue={`${DOBYear || 'YYYY'}`}
              menuData={getDobDropDownOptions(DateOfBirth.Years)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

ContactFormSection.propTypes = {
  Account: PropTypes.object,
  Prefixes: PropTypes.array,
  DateOfBirth: PropTypes.object,
  setSelectedPrefix: PropTypes.func,
  setSelectedDobDay: PropTypes.func,
  setSelectedDobMonth: PropTypes.func,
  setSelectedDobYear: PropTypes.func,
  setUpdateEmail: PropTypes.func,
  requestChange: PropTypes.bool,
  setRequestChange: PropTypes.func,
  retainPasswordFocus: PropTypes.bool,
  setRetainPasswordFocus: PropTypes.func,
};
